const DIRECT_GENERATION_PATTERNS = [
  /\bgenerate directly\b/i,
  /\bjust generate\b/i,
  /\bskip (?:the )?(?:interview|questions?)\b/i,
  /\b(?:do not|don't) ask (?:me )?(?:anything|questions?)\b/i,
  /\b(?:do not|don't) show (?:me )?(?:the )?review\b/i,
  /\bfinish (?:off )?(?:the )?generation\b/i,
  /\bmake (?:the details|it|things) up\b/i,
  /\bproceed with (?:the )?(?:limited|little|minimal) information\b/i,
  /\b(?:everything|the plan|all details) (?:is|are) final(?:ized)?\b/i,
]

export const shouldBypassAiInterview = (transcript: string): boolean =>
  DIRECT_GENERATION_PATTERNS.some((pattern) => pattern.test(transcript))

const isUsableDate = (value: unknown) =>
  (typeof value === 'string' || typeof value === 'number') && !Number.isNaN(new Date(value).getTime())

export const findMissingTimelineDates = (goalInput: unknown): string[] => {
  if (!goalInput || typeof goalInput !== 'object' || Array.isArray(goalInput)) return ['Goal']
  const goal = goalInput as Record<string, unknown>
  const missing: string[] = []
  const check = (record: Record<string, unknown>, label: string) => {
    if (!isUsableDate(record.startDate) || !isUsableDate(record.endDate)) missing.push(label)
  }
  check(goal, `Goal "${typeof goal.title === 'string' ? goal.title : 'Untitled'}"`)
  const tasks = Array.isArray(goal.tasks) ? goal.tasks : []
  tasks.forEach((task, taskIndex) => {
    if (!task || typeof task !== 'object' || Array.isArray(task)) return
    const taskRecord = task as Record<string, unknown>
    check(taskRecord, `Task "${typeof taskRecord.title === 'string' ? taskRecord.title : taskIndex + 1}"`)
    const subtasks = Array.isArray(taskRecord.subTasks) ? taskRecord.subTasks : []
    subtasks.forEach((subtask, subtaskIndex) => {
      if (!subtask || typeof subtask !== 'object' || Array.isArray(subtask)) return
      const subtaskRecord = subtask as Record<string, unknown>
      check(subtaskRecord, `Subtask "${typeof subtaskRecord.title === 'string' ? subtaskRecord.title : subtaskIndex + 1}"`)
    })
  })
  return missing
}

const hasChecklistItems = (record: Record<string, unknown>): boolean => {
  const list = record.checklist
  if (!Array.isArray(list)) return false
  return list.some((entry) => entry && typeof entry === 'object')
}

/**
 * Returns a list of container labels (Goal/Task/Subtask) that have zero
 * checklist items. Checklists are the coin-of-the-realm for progress in
 * Timelining — a plan without checklists is an outline the user cannot act
 * on — so a non-empty result means the draft is not ready to persist.
 *
 * Only surfaces up to 3 labels; the caller shows them in the ASK message.
 */
export const findEmptyChecklistContainers = (goalInput: unknown): string[] => {
  if (!goalInput || typeof goalInput !== 'object' || Array.isArray(goalInput)) return []
  const goal = goalInput as Record<string, unknown>
  const tasks = Array.isArray(goal.tasks) ? goal.tasks : []
  const emptyLabels: string[] = []
  const goalHasChecklists = hasChecklistItems(goal)
  let anyTaskHasChecklists = false
  let anySubtaskHasChecklists = false
  tasks.forEach((task, taskIndex) => {
    if (!task || typeof task !== 'object' || Array.isArray(task)) return
    const taskRecord = task as Record<string, unknown>
    const taskTitle = typeof taskRecord.title === 'string' ? taskRecord.title : `#${taskIndex + 1}`
    const taskHasOwn = hasChecklistItems(taskRecord)
    if (taskHasOwn) anyTaskHasChecklists = true
    const subtasks = Array.isArray(taskRecord.subTasks) ? taskRecord.subTasks : []
    let anyChildHasOwn = false
    subtasks.forEach((subtask, subtaskIndex) => {
      if (!subtask || typeof subtask !== 'object' || Array.isArray(subtask)) return
      const subtaskRecord = subtask as Record<string, unknown>
      const subTitle = typeof subtaskRecord.title === 'string' ? subtaskRecord.title : `#${subtaskIndex + 1}`
      if (hasChecklistItems(subtaskRecord)) {
        anyChildHasOwn = true
        anySubtaskHasChecklists = true
      } else if (emptyLabels.length < 3) {
        emptyLabels.push(`Subtask "${subTitle}"`)
      }
    })
    // A task without own checklists is OK ONLY when at least one of its
    // subtasks carries them.
    if (!taskHasOwn && !anyChildHasOwn && emptyLabels.length < 3) {
      emptyLabels.push(`Task "${taskTitle}"`)
    }
  })
  // If NO container across the whole tree carries any checklist item, surface
  // the goal itself so the caller can ask a targeted follow-up.
  if (!goalHasChecklists && !anyTaskHasChecklists && !anySubtaskHasChecklists) {
    const goalTitle = typeof goal.title === 'string' ? goal.title : 'Untitled'
    return [`Goal "${goalTitle}"`, ...emptyLabels].slice(0, 3)
  }
  return emptyLabels
}
